import { Amplify } from "aws-amplify";
import { signOut } from "aws-amplify/auth";

/**
 * Old (legacy account) Cognito pool ids. A token whose issuer references one of
 * these was minted by the OLD pool and belongs to a user who has not yet been
 * lazily migrated into the new Gen2 pool.
 */
const OLD_POOL_IDS = [
  "us-west-1_uzat5qPSM", // old production pool
  "us-west-1_Yq2Qr8mTE", // old develop pool
] as const;

/**
 * Old (legacy account) Cognito app client ids. Amplify stores tokens in
 * localStorage under keys namespaced by the *client id*
 * (`CognitoIdentityServiceProvider.<clientId>.*`). After a cutover the new-pool
 * SDK only reads keys under the NEW client id, so any tokens left under an OLD
 * client id become orphaned junk the SDK silently ignores — they never get
 * cleared and never trigger the session check below. We sweep them explicitly.
 */
const OLD_CLIENT_IDS = [
  "2o2gs0h9cditq91pmrbv53vs8r", // old production web client
  "524j6rokvq80n13ml1od1f6a44", // old develop web client
  "59acsqo48hi63n94jonop20o2c", // old develop native client
  "36mjbadpqj2npk10o78t62up1a", // old mark client (defensive)
] as const;

function frontendTargetsOldPool(): boolean {
  const configuredPoolId = Amplify.getConfig().Auth?.Cognito?.userPoolId ?? "";
  return OLD_POOL_IDS.some((poolId) => configuredPoolId === poolId);
}

/** Decode a JWT payload without verifying the signature. */
function decodeJwtPayload(token: string): Record<string, unknown> {
  const parts = token.split(".");
  if (parts.length < 2) return {};
  try {
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(b64)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/**
 * Remove orphaned Amplify token entries in localStorage that were issued by an
 * OLD pool. These accumulate when a user had a live old-pool session at cutover:
 * the new-pool SDK stores its own session under the new client id and ignores
 * the old one, leaving stale tokens behind. Returns the number of keys removed.
 *
 * Matched conservatively: a key is removed only if it is a
 * `CognitoIdentityServiceProvider.<clientId>.*` entry AND either (a) the client
 * id is a known old client, or (b) an adjacent idToken decodes to an old-pool
 * issuer. Never touches keys for the currently-configured (new) client.
 */
export function sweepOrphanedOldPoolTokens(): number {
  if (typeof window === "undefined" || !window.localStorage) return 0;
  if (frontendTargetsOldPool()) return 0; // inert during rollback

  const configuredClientId =
    Amplify.getConfig().Auth?.Cognito?.userPoolClientId ?? "";

  const prefix = "CognitoIdentityServiceProvider.";
  const keysToRemove: string[] = [];

  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (!key || !key.startsWith(prefix)) continue;

    const clientId = key.slice(prefix.length).split(".")[0];
    if (!clientId || clientId === configuredClientId) continue; // never the active client

    const isKnownOldClient = OLD_CLIENT_IDS.some((c) => c === clientId);

    // If it's an idToken entry, confirm via issuer; otherwise rely on client id.
    let issuedByOldPool = isKnownOldClient;
    if (!issuedByOldPool && key.endsWith(".idToken")) {
      const raw = window.localStorage.getItem(key);
      if (raw) {
        const iss = decodeJwtPayload(raw).iss;
        if (typeof iss === "string") {
          issuedByOldPool = OLD_POOL_IDS.some((p) => iss.includes(p));
        }
      }
    }

    if (issuedByOldPool) keysToRemove.push(key);
  }

  for (const key of keysToRemove) window.localStorage.removeItem(key);

  if (keysToRemove.length > 0) {
    console.log(
      `[migration] Cleared ${keysToRemove.length} orphaned old-pool token entries from localStorage.`,
    );
  }
  return keysToRemove.length;
}

/**
 * Migration issuer-gate.
 *
 * When the frontend targets a NEW Gen2 pool, a session that was issued by an OLD
 * pool must be cleared so the user re-authenticates. That re-auth (now via
 * USER_PASSWORD_AUTH) fires the UserMigration trigger and transparently creates
 * the user in the new pool.
 *
 * This runs in two ways:
 *  1. `runMigrationGateOnStartup()` — called once at app boot, independent of
 *     authenticator state, to sweep orphaned old-pool tokens the SDK ignores.
 *  2. `enforceMigrationIssuerGate()` — called from UserContext for the case where
 *     the SDK DID surface an old-pool session; forces a clean signOut.
 *
 * CRITICAL SAFETY GUARD (this is what the Aug 2026 lockout got wrong):
 * Only act when the frontend is NOT itself pointed at an old pool. During a
 * rollback the frontend is re-pinned to the old pool, so this gate is inert and
 * cannot lock users out in a loop. The previous version signed out on issuer
 * alone, which force-logged-out every user once prod was pinned back.
 *
 * Remove this gate after the old pool is decommissioned (migration soak done).
 */
export async function enforceMigrationIssuerGate(): Promise<boolean> {
  try {
    if (frontendTargetsOldPool()) return false;

    // Read the issuer from the ACTIVE client's stored idToken directly, rather
    // than calling fetchAuthSession(). fetchAuthSession() also resolves Identity
    // Pool (guest) credentials, which throws a noisy 400
    // "Unauthenticated access is not supported for this identity pool" on
    // anonymous page loads. We only need the idToken issuer, which is in
    // localStorage.
    const issuer = readActiveSessionIssuer();
    if (!issuer) return false; // no active user-pool session → nothing to gate

    const issuedByOldPool = OLD_POOL_IDS.some((poolId) =>
      issuer.includes(poolId),
    );
    if (issuedByOldPool) {
      console.log(
        "[migration] Session issued by old Cognito pool while app targets new pool — forcing re-authentication to trigger migration.",
      );
      await signOut();
      return true;
    }
    return false;
  } catch (err) {
    console.warn("[migration] issuer gate check failed, continuing:", err);
    return false;
  }
}

/**
 * Read the issuer (`iss`) from the idToken stored under the CURRENTLY-CONFIGURED
 * Cognito client id in localStorage. Returns undefined when no session is stored.
 * Avoids fetchAuthSession()'s identity-pool credential resolution (which errors
 * for anonymous users on pools without guest access).
 */
function readActiveSessionIssuer(): string | undefined {
  if (typeof window === "undefined" || !window.localStorage) return undefined;
  const clientId = Amplify.getConfig().Auth?.Cognito?.userPoolClientId ?? "";
  if (!clientId) return undefined;

  const prefix = `CognitoIdentityServiceProvider.${clientId}.`;
  const lastUser = window.localStorage.getItem(`${prefix}LastAuthUser`);
  if (!lastUser) return undefined;

  const raw = window.localStorage.getItem(`${prefix}${lastUser}.idToken`);
  if (!raw) return undefined;

  const iss = decodeJwtPayload(raw).iss;
  return typeof iss === "string" ? iss : undefined;
}

/**
 * App-startup entrypoint: sweep orphaned old-pool tokens, then run the session
 * gate. Safe to call before/after Amplify.configure and regardless of login
 * state. Returns true if the active session was cleared (signed out).
 */
export async function runMigrationGateOnStartup(): Promise<boolean> {
  sweepOrphanedOldPoolTokens();
  return enforceMigrationIssuerGate();
}
